import { App, Button, Flex, Modal, type TableColumnsType, theme, Typography } from 'antd';
import { type SortOrder } from 'antd/es/table/interface';
import { useQuery } from 'jsonapi-react';
import { CircleAlert, CircleCheck, CircleX, PlusCircle, Trash2 } from 'lucide-react';
import PropTypes from 'prop-types';
import { type Key, type ReactNode, useReducer, useState } from 'react';
import { from, switchMap, throwError, of, tap, catchError, EMPTY } from 'rxjs';
import AgenciesFilter from './agencies.filter';
import AgencyForm from './agency.form';
import AuthorizedComponent from '../../../../components/authorized-component';
import { DataTable } from '../../../../components/data-table';
import SyncSuspense from '../../../../components/sync-suspense';
import { ExtraContent } from '../../../../contexts/extra-content-context-provider';
import { Agency } from '../../../../entities';
import useDebouncedState from '../../../../hooks/use-debounced-state';
import { Roles } from '../../../../types';

export interface AgencyFiltersProps {
  onChange?: (filters: FiltersState) => void;
  initialFilters: FiltersState;
}

interface FiltersState {
  query: string;
  showInActive: boolean;
}

interface AgencyPageState {
  filters: AgencyFiltersProps['initialFilters']; // TODO: To be replaced
  pagination: { current: number; pageSize: number };
  sort: { order?: SortOrder; field?: Key | readonly Key[] };
}

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_SORT_FIELD = 'name';
const DEFAULT_SORT_DIRECTION: SortOrder = 'descend';
const SUCCESS_DELETE_MESSAGE = 'Agency deleted successfully';

const serializeSort = (sort: AgencyPageState['sort']): string | undefined => {
  if (sort.field && sort.order) {
    const field = Array.isArray(sort.field) ? sort.field.join(',') : sort.field.toString();
    return `${sort.order === 'descend' ? '-' : ''}${field}`;
  }
  return undefined;
};

const serializeFilters = (filters: AgencyPageState['filters']): string | undefined => {
  const parts: string[] = [];
  if (filters?.query) parts.push(`name=ini='*${filters.query}*'`);
  if (filters?.showInActive) parts.push(`isActive=='${!filters?.showInActive}'`);

  return parts.length ? parts.join(';') : undefined;
};

const reducer = (
  state: AgencyPageState,
  action: { type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT'; payload?: any }
): AgencyPageState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: { query: '', showInActive: false } };
    case 'SET_FILTER':
      return {
        ...state,
        filters: action.payload,
        pagination: {
          ...state?.pagination,
          current: DEFAULT_PAGE_NUMBER,
        },
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload,
      };
    case 'SET_SORT':
      return {
        ...state,
        sort: action.payload as AgencyPageState['sort'],
      };
  }
};
const AgenciesPage = (): ReactNode => {
  const { token } = theme.useToken();
  const { notification } = App.useApp();
  const [selectedRowKey, setSelectedRowKey] = useState<string>();
  const [editedAgency, setEditedAgency] = useState<Agency | null>(null);
  const [state, setState] = useReducer(reducer, {
    filters: { query: '', showInActive: false },
    pagination: { current: DEFAULT_PAGE_NUMBER, pageSize: DEFAULT_PAGE_SIZE },
    sort: { field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_DIRECTION },
  });
  const [debouncedState] = useDebouncedState({ state, time: 1000 });
  const agencies = useQuery<Agency[]>(
    debouncedState && [
      'agency',
      {
        filter: serializeFilters(debouncedState.filters),
        page: {
          size: debouncedState.pagination.pageSize,
          number: debouncedState.pagination.current,
          totals: 1,
        },
        sort: serializeSort(debouncedState.sort),
      },
    ]
  );

  const columns: TableColumnsType<Agency> = [
    { title: '', width: '50px' },
    {
      title: 'Name',
      dataIndex: ['name'],
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
      render: (value, item) => (
        <Typography.Link
          onClick={() => {
            setSelectedRowKey(item.id);
            setEditedAgency(item);
          }}
        >
          {value}
        </Typography.Link>
      ),
    },
    {
      title: 'Description',
      dataIndex: ['description'],
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'Status',
      render: (_, record) =>
        record.isActive ? (
          <Flex align="center" gap="small">
            <CircleCheck style={{ color: token.colorTextDisabled }} />
            <Typography.Text>Active</Typography.Text>
          </Flex>
        ) : (
          <Flex align="center" gap="small">
            <CircleX style={{ color: token.colorTextDisabled }} />
            <Typography.Text>Inactive</Typography.Text>
          </Flex>
        ),
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
  ];

  return (
    <>
      <DataTable<Agency>
        title="Agencies"
        description="List of agencies assignable to sites"
        filtersNode={
          <AgenciesFilter
            initialFilters={state.filters}
            onChange={(filters) => {
              setState({ type: 'SET_FILTER', payload: filters });
            }}
          />
        }
        actionsNode={
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <Button
              type="primary"
              icon={<PlusCircle />}
              onClick={() => {
                setEditedAgency(new Agency());
              }}
            >
              Add Agency
            </Button>
          </AuthorizedComponent>
        }
        selectedRowKeys={selectedRowKey ? [selectedRowKey] : undefined}
        dataList={agencies}
        onChange={setState}
        columns={columns}
        actions={[
          {
            key: 'delete',
            label: 'Delete',
            icon: <Trash2 />,
            danger: true,
            onItemClick: (record) => {
              Modal.confirm({
                width: '50%',
                title: 'Confirm deletion',
                icon: <CircleAlert />,
                content: (
                  <Typography.Text>
                    Are you sure you want to delete <b>{record.name}</b> permanently?
                  </Typography.Text>
                ),
                okText: 'Yes',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => {
                  from(agencies.client.delete(['agency', record.id]))
                    .pipe(
                      switchMap((res) =>
                        res.error
                          ? throwError(() => new Error(res.error?.detail ?? res.error?.title))
                          : of(res.data)
                      ),
                      tap(() => {
                        notification.success({ message: ' ', description: SUCCESS_DELETE_MESSAGE });
                        agencies.refetch?.();
                      }),
                      catchError((error: Error) => {
                        notification.error({
                          message: 'Failed to delete agency',
                          description: error.message,
                        });
                        return EMPTY;
                      })
                    )
                    .subscribe();
                },
              });
            },
          },
        ]}
      />
      <SyncSuspense condition={editedAgency}>
        <ExtraContent>
          <AgencyForm
            agency={editedAgency ?? new Agency()}
            onSuccess={() => {
              agencies.refetch?.();
              setEditedAgency(null);
            }}
            onCancel={() => {
              setEditedAgency(null);
            }}
          />
        </ExtraContent>
      </SyncSuspense>
    </>
  );
};

AgenciesPage.propTypes = {
  defaultPageNumber: PropTypes.number,
  defaultPageSize: PropTypes.number,
};

AgenciesPage.defaultProps = {
  defaultPageNumber: 1,
  defaultPageSize: 10,
  onVisibleColumnsChange: () => {},
  onSelectRowsChange: () => {},
};

AgenciesPage.displayName = 'AgenciesPage';
export default AgenciesPage;
